import React, { useEffect, useMemo, useState } from "react";
import { getCiActions, dismissCiAction } from "../../api/competitiveIntelligence";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";
import { Skeleton } from "../shared/Skeleton";
import { MONTHS } from "./shared";

// "2026-07-03" → "Jul 3, 2026". Parsed as a plain date string to avoid UTC timezone shift.
const fmtExpires = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-");
  return `${MONTHS[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
};

// "2026-04-27T23:46:44.379588+00:00" → "Apr 27, 2026". Generated_at is a real
// timestamp (unlike expires_at which is date-only), so we use the Date parser.
const fmtGenerated = (iso) => {
  if (!iso) return null;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;
  return `${MONTHS[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
};

// "2026-07-03" + "2026-07-05" → "Jul 3–5"; same start/end → "Jul 3"; crosses months → "Jul 31 – Aug 2".
// Parsed as plain date strings to avoid UTC timezone shift.
const fmtRange = (start, end) => {
  if (!start) return null;
  const [, sm, sd] = start.split("-").map((part) => parseInt(part, 10));
  if (!end || end === start) return `${MONTHS[sm - 1]} ${sd}`;
  const [, em, ed] = end.split("-").map((part) => parseInt(part, 10));
  if (sm === em) return `${MONTHS[sm - 1]} ${sd}–${ed}`;
  return `${MONTHS[sm - 1]} ${sd} – ${MONTHS[em - 1]} ${ed}`;
};

const fmtMoneyWhole = (value) => (value == null ? null : `$${Math.round(Number(value))}`);

const fmtDelta = (delta) => {
  if (delta == null) return null;
  const abs = Math.abs(Number(delta));
  if (Number(delta) > 0) return `+$${Math.round(abs)}`;
  if (Number(delta) < 0) return `−$${Math.round(abs)}`;
  return `$0`;
};

// Extracts the headline pieces from supporting_data. Returns null when there's
// not enough structured data — the card then falls back to prose-only layout
// (today's behaviour). Each downstream piece (range, delta, signal) is
// independently optional so the headline progressively enhances as backend
// fields come online. Contract documented in competitive-intelligence-ui-audit.md
// under the AI Pricing Actions restructure.
const buildHeadline = (supportingData) => {
  if (!supportingData || typeof supportingData !== "object") return null;
  const currentRate = supportingData.current_rate;
  const suggestedRate = supportingData.suggested_rate;
  if (currentRate == null || suggestedRate == null) return null;
  const delta = supportingData.delta_dollars ?? Number(suggestedRate) - Number(currentRate);
  return {
    current_rate: currentRate,
    suggested_rate: suggestedRate,
    delta,
    range: fmtRange(supportingData.target_start, supportingData.target_end),
    key_signal: typeof supportingData.key_signal === "string" && supportingData.key_signal.trim() ? supportingData.key_signal.trim() : null,
  };
};

// Each action_type drives the left-border colour, the tinted card surface,
// the action-type chip, the dot, and the human label.
// The five action_type values + colors are fixed by spec ("Action Type Colors"):
//   raise_rate → red #EF4444 · lower_rate → blue #3B82F6 · hold_rate → green #22C55E
//   add_premium → orange #F59E0B · watch → gray #9CA3AF
// Any unrecognized type falls through to styleFor()'s gray default.
const ACTION_STYLES = {
  raise_rate: {
    border: "border-l-red-500",
    surface: "bg-red-50/60 dark:bg-red-500/5",
    chipBg: "bg-red-100 dark:bg-red-500/15",
    chipText: "text-red-700 dark:text-red-300",
    dot: "bg-red-500",
    label: "Raise Rate",
  },
  lower_rate: {
    border: "border-l-blue-500",
    surface: "bg-blue-50/60 dark:bg-blue-500/5",
    chipBg: "bg-blue-100 dark:bg-blue-500/15",
    chipText: "text-blue-700 dark:text-blue-300",
    dot: "bg-blue-500",
    label: "Lower Rate",
  },
  hold_rate: {
    border: "border-l-green-500",
    surface: "bg-green-50/60 dark:bg-green-500/5",
    chipBg: "bg-green-100 dark:bg-green-500/15",
    chipText: "text-green-700 dark:text-green-300",
    dot: "bg-green-500",
    label: "Hold Rate",
  },
  add_premium: {
    border: "border-l-amber-500",
    surface: "bg-amber-50/60 dark:bg-amber-500/5",
    chipBg: "bg-amber-100 dark:bg-amber-500/15",
    chipText: "text-amber-700 dark:text-amber-300",
    dot: "bg-amber-500",
    label: "Add Premium",
  },
  watch: {
    border: "border-l-gray-400",
    surface: "bg-gray-50/60 dark:bg-gray-500/5",
    chipBg: "bg-gray-100 dark:bg-gray-500/15",
    chipText: "text-gray-700 dark:text-gray-300",
    dot: "bg-gray-400",
    label: "Watch",
  },
};

const styleFor = (type) =>
  ACTION_STYLES[type] ?? {
    border: "border-l-gray-400",
    surface: "bg-gray-50/60 dark:bg-gray-500/5",
    chipBg: "bg-gray-100 dark:bg-gray-500/15",
    chipText: "text-gray-700 dark:text-gray-300",
    dot: "bg-gray-400",
    label: type ? type.replace(/_/g, " ") : "Action",
  };

// Priority 1 = red (most urgent), 2 = amber, 3 = blue, 4+ = gray. Maps to spec
// red #EF4444 / orange #F59E0B / blue #3B82F6 / gray #9CA3AF.
const priorityTextClass = (priority) => {
  if (priority === 1) return "text-red-600 dark:text-red-400";
  if (priority === 2) return "text-amber-600 dark:text-amber-400";
  if (priority === 3) return "text-blue-600 dark:text-blue-400";
  return "text-gray-500 dark:text-gray-400";
};

const HeaderRow = ({ count }) => (
  <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-start justify-between gap-3 flex-wrap">
    <div>
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center">
        AI Pricing Actions
        <InfoTip text="Specific rate-change suggestions generated by AI from your latest sync, ranked by priority. Each card names the affected dates, the competitor signal driving it, and a suggested dollar move." />
      </h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
        Recommended rate moves generated from your latest sync, ordered by priority.
      </p>
    </div>
    {count > 0 && (
      <div className="text-xs text-gray-500 dark:text-gray-400">
        <span className="font-semibold text-gray-700 dark:text-gray-200">{count}</span> open
      </div>
    )}
  </div>
);

const ActionCard = ({ action, dismissing, onDismiss }) => {
  const style = styleFor(action.action_type);
  const expires = fmtExpires(action.expires_at);
  const generated = fmtGenerated(action.generated_at);
  const headline = buildHeadline(action.supporting_data);
  return (
    <div
      className={`group relative bg-white dark:bg-gray-900 rounded-lg shadow-sm dark:shadow-md ring-1 ring-gray-200 dark:ring-gray-700/60 border-l-[3px] ${style.border} pl-5 pr-5 pt-4 pb-4 transition-shadow hover:shadow-md`}
    >
      <button
        type="button"
        disabled={dismissing}
        onClick={() => onDismiss(action.id)}
        aria-label="Dismiss action"
        title="Dismiss"
        className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 opacity-60 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]/40 transition"
      >
        {dismissing ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="animate-spin">
            <path d="M21 12a9 9 0 1 1-6.2-8.55" strokeLinecap="round" />
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        )}
      </button>

      {/* Title row: title on the left, dismiss × is absolute top-right
          (pr-9 reserves space). */}
      <div className="pr-9">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 min-w-0">
          {action.title || "—"}
        </h3>
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 flex-wrap">
          {action.category_code && <span className="uppercase tracking-wide">{action.category_code}</span>}
          {action.category_code && generated && <span className="text-gray-300 dark:text-gray-700" aria-hidden="true">·</span>}
          {generated && <span>Generated {generated}</span>}
        </div>
      </div>

      {/* Structured headline — only rendered when supporting_data carries the
          minimum from/to fields. Surfaces the decision-useful numbers
          (current → suggested · range · ±delta · because signal) so the user
          doesn't have to mine the prose paragraph below. Progressive: range
          and key_signal are independently optional. */}
      {headline && (
        <div className="mt-3 flex items-baseline gap-x-2 gap-y-1 flex-wrap text-[15px] tabular-nums">
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            <span className="text-gray-500 dark:text-gray-400 font-normal text-sm">From </span>
            {fmtMoneyWhole(headline.current_rate)}
            <span className="mx-1.5 text-gray-400 dark:text-gray-500" aria-hidden="true">→</span>
            {fmtMoneyWhole(headline.suggested_rate)}
          </span>
          {headline.range && (
            <>
              <span className="text-gray-300 dark:text-gray-700" aria-hidden="true">·</span>
              <span className="text-sm text-gray-700 dark:text-gray-300">{headline.range}</span>
            </>
          )}
          {headline.delta != null && (
            <>
              <span className="text-gray-300 dark:text-gray-700" aria-hidden="true">·</span>
              <span className={`text-sm font-semibold ${style.chipText}`}>{fmtDelta(headline.delta)}</span>
            </>
          )}
          {headline.key_signal && (
            <>
              <span className="text-gray-300 dark:text-gray-700" aria-hidden="true">·</span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                <span className="text-gray-400 dark:text-gray-500">because </span>{headline.key_signal}
              </span>
            </>
          )}
        </div>
      )}

      {/* Body + meta sit in a flex row aligned to the bottom: the body
          paragraph (or collapsed "Why" toggle when a headline is present)
          takes the available width, the meta block hugs the bottom-right
          corner. shrink-0 keeps the meta on one line. */}
      <div className="mt-3 flex flex-col md:flex-row md:items-end md:justify-between gap-3 md:gap-6">
        {action.body ? (
          headline ? (
            <details className="group min-w-0 md:basis-3/4 md:max-w-[60%]">
              <summary className="cursor-pointer inline-flex items-center text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 select-none list-none [&::-webkit-details-marker]:hidden">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mr-1 transition-transform group-open:rotate-90" aria-hidden="true">
                  <polyline points="9 6 15 12 9 18" />
                </svg>
                Why
              </summary>
              <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300 whitespace-pre-line">
                {action.body}
              </p>
            </details>
          ) : (
            <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300 whitespace-pre-line min-w-0 md:basis-3/4 md:max-w-[60%]">
              {action.body}
            </p>
          )
        ) : (
          <span className="flex-1" aria-hidden="true" />
        )}
        <div className="shrink-0 flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400 flex-wrap">
          {expires && <span className="whitespace-nowrap">Expires {expires}</span>}
          {expires && <span className="text-gray-300 dark:text-gray-700" aria-hidden="true">·</span>}
          <span className={`font-semibold tabular-nums text-xs ${priorityTextClass(action.priority)}`} title={`Priority ${action.priority ?? "—"}`}>
            P{action.priority ?? "—"}
          </span>
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${style.dot}`} aria-hidden="true" />
          <span className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${style.chipBg} ${style.chipText}`}>
            {style.label}
          </span>
        </div>
      </div>
    </div>
  );
};

// P3+ cards collapse into compact one-liners so the high-priority actions get the visual
// weight. Each row carries enough to decide whether to expand: title, type chip, expires,
// dismiss × . Clicking the row expands it into a full ActionCard inline. Audit pass 2 #2:
// the wall of five same-color RAISE RATE cards was reading as spam — tiering by priority
// keeps the page scannable when 100% of actions share a type.
const CompactActionRow = ({ action, dismissing, onDismiss, onExpand }) => {
  const style = styleFor(action.action_type);
  const expires = fmtExpires(action.expires_at);
  return (
    <div
      className={`flex items-center gap-3 px-4 py-2.5 rounded-md bg-white dark:bg-gray-900 ring-1 ring-gray-200 dark:ring-gray-700/60 border-l-[3px] ${style.border} hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors`}
    >
      <span className={`shrink-0 text-xs font-semibold tabular-nums ${priorityTextClass(action.priority)}`} title={`Priority ${action.priority ?? "—"}`}>
        P{action.priority ?? "—"}
      </span>
      <button
        type="button"
        onClick={() => onExpand(action.id)}
        aria-label={`Expand details for ${action.title || "action"}`}
        title="Click to expand"
        className="group/expand flex-1 min-w-0 text-left flex items-center gap-2 focus:outline-none"
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="shrink-0 text-gray-400 dark:text-gray-500 group-hover/expand:text-gray-700 dark:group-hover/expand:text-gray-200 transition-colors"
          aria-hidden="true"
        >
          <polyline points="9 6 15 12 9 18" />
        </svg>
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate group-hover/expand:underline decoration-dotted underline-offset-2">
          {action.title || "—"}
        </span>
      </button>
      <span className={`shrink-0 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${style.chipBg} ${style.chipText}`}>
        {style.label}
      </span>
      {expires && (
        <span className="shrink-0 hidden sm:inline text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
          Expires {expires}
        </span>
      )}
      <button
        type="button"
        disabled={dismissing}
        onClick={() => onDismiss(action.id)}
        aria-label="Dismiss action"
        title="Dismiss"
        className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]/40 transition"
      >
        {dismissing ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="animate-spin">
            <path d="M21 12a9 9 0 1 1-6.2-8.55" strokeLinecap="round" />
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        )}
      </button>
    </div>
  );
};

// Preserves the left-border priority stripe that brands real ActionCards, so the
// loading shape matches the rendered shape rather than a generic card.
const SkeletonCard = () => (
  <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 border-l-[4px] border-l-gray-200 dark:border-l-gray-700 p-5">
    <Skeleton className="h-3 w-24" />
    <Skeleton className="h-4 w-2/3 mt-3" />
    <Skeleton className="h-3 w-full mt-3" />
    <Skeleton className="h-3 w-5/6 mt-2" />
  </div>
);

// Priority cutoff: priorities at or below this number render as full ActionCards. Higher
// priority numbers (P3+) render as compact rows, expandable on click.
const FULL_CARD_PRIORITY_THRESHOLD = 2;

const AiPricingActionsPanel = ({ clientId }) => {
  const [state, setState] = useState({ loading: false, error: "", data: null });
  // Track locally-dismissed ids so the card disappears immediately even if the backend
  // dismiss route hasn't been wired up yet (it'd otherwise reappear on next refetch).
  const [dismissedIds, setDismissedIds] = useState(() => new Set());
  const [dismissingId, setDismissingId] = useState(null);
  // Ids of P3+ compact rows the user has clicked to expand. Resets per client.
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setState({ loading: true, error: "", data: null });
    setDismissedIds(new Set());
    setExpandedIds(new Set());
    getCiActions(clientId)
      .then((resp) => {
        if (!cancelled) setState({ loading: false, error: "", data: Array.isArray(resp?.data) ? resp.data : [] });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, error: err?.message || "Failed to load actions", data: null });
      });
    return () => { cancelled = true; };
  }, [clientId]);

  const visibleActions = useMemo(() => {
    const rows = Array.isArray(state.data) ? state.data : [];
    const filtered = rows.filter((action) => !dismissedIds.has(action.id));
    return filtered
      .slice()
      .sort((actionA, actionB) => (actionA.priority ?? 999) - (actionB.priority ?? 999));
  }, [state.data, dismissedIds]);

  const handleToggleExpand = (actionId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(actionId)) next.delete(actionId);
      else next.add(actionId);
      return next;
    });
  };

  const handleDismiss = async (actionId) => {
    if (!actionId || dismissingId) return;
    setDismissingId(actionId);
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(actionId);
      return next;
    });
    try {
      await dismissCiAction(clientId, actionId);
    } catch (err) {
      // Backend dismiss isn't wired up yet — keep the optimistic removal but surface in console.
      console.warn("dismissCiAction failed:", err?.message || err);
    } finally {
      setDismissingId(null);
    }
  };

  if (state.loading) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
        <HeaderRow count={0} />
        <div className="p-4 sm:p-5 space-y-3">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
        <HeaderRow count={0} />
        <ErrorState variant="inset" padding="px-5 py-6" message={state.error} />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
      <HeaderRow count={visibleActions.length} />
      <div className="px-4 py-3 sm:px-5">
        {visibleActions.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
            No open pricing actions. New recommendations are generated after each daily sync.
          </div>
        ) : (
          <div className="space-y-3">
            {/* P1-P2: full ActionCard. Highest priority gets the visual weight. */}
            {visibleActions
              .filter((action) => (action.priority ?? 999) <= FULL_CARD_PRIORITY_THRESHOLD)
              .map((action) => (
                <ActionCard
                  key={action.id}
                  action={action}
                  dismissing={dismissingId === action.id}
                  onDismiss={handleDismiss}
                />
              ))}

            {/* P3+: compact rows by default, expanded inline on click. Keeps the panel
                scannable when 100% of actions are the same color/type. */}
            {visibleActions
              .filter((action) => (action.priority ?? 999) > FULL_CARD_PRIORITY_THRESHOLD)
              .map((action) =>
                expandedIds.has(action.id) ? (
                  <ActionCard
                    key={action.id}
                    action={action}
                    dismissing={dismissingId === action.id}
                    onDismiss={handleDismiss}
                  />
                ) : (
                  <CompactActionRow
                    key={action.id}
                    action={action}
                    dismissing={dismissingId === action.id}
                    onDismiss={handleDismiss}
                    onExpand={handleToggleExpand}
                  />
                )
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AiPricingActionsPanel;
